import { Property, type PropertyList, String$ } from '@kalos/kalos-rpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { PropertyClientService } from '../../tools/helpers';
import { queryKeys } from './constants';
import { type EntityFilter } from './utils';

export type PropertyFilter = EntityFilter<Property>;
export const usePropertiesBatchGetQuery = <TSelectData = PropertyList,>({
  filter = {},
  enabled = true,
  select,
}: {
  filter?: PropertyFilter;
  enabled?: boolean;
  select?: (data?: PropertyList) => TSelectData;
} = {}) => {
  return useQuery({
    queryFn: () => {
      const req = Property.create(filter);
      return PropertyClientService.BatchGet(req);
    },
    queryKey: [
      queryKeys.properties.root,
      queryKeys.properties.list,
      queryKeys.properties.getHash(filter),
    ],
    enabled,
    select,
  });
};

export const usePropertiesGetQuery = <TSelectData = Property,>({
  filter = {},
  enabled = true,
  select,
}: {
  filter?: PropertyFilter;
  enabled?: boolean;
  select?: (data?: Property) => TSelectData;
} = {}) => {
  return useQuery({
    queryFn: () => {
      const req = Property.create(filter);
      return PropertyClientService.Get(req);
    },
    queryKey: [queryKeys.properties.root, queryKeys.properties.getHash(filter)],
    enabled,
    select,
  });
};

export const usePropertyQueryInline = () => {
  const queryClient = useQueryClient();

  const fetch = useCallback(
    async ({ filter = {} }: { filter?: PropertyFilter } = {}) => {
      const queryKey = [queryKeys.properties.root, queryKeys.properties.getHash(filter)];
      const cache = queryClient.getQueryData<Property>(queryKey);
      if (!cache) {
        const req = Property.create(filter);
        const event = await PropertyClientService.Get(req);
        if (!event) throw new Error('Property not found');
        queryClient.setQueryData(queryKey, event);
        return event;
      }
      return cache;
    },
    [queryClient],
  );

  return useMemo(() => ({ fetch }), [fetch]);
};

export const usePropertiesSearchQuery = <TSelectData = PropertyList,>({
  searchTerm = '',
  enabled = true,
  select,
}: {
  searchTerm?: string;
  enabled?: boolean;
  select?: (data?: PropertyList) => TSelectData;
} = {}) => {
  return useQuery({
    queryFn: () => {
      const req = String$.create({ value: searchTerm });
      return PropertyClientService.SearchProperties(req);
    },
    queryKey: [queryKeys.properties.root, queryKeys.properties.search, searchTerm],
    enabled: enabled && searchTerm.length >= 2,
    select,
  });
};

export const usePropertyUpdateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (req: Property) => {
      return await PropertyClientService.Update(req);
    },
    onSuccess: async (data) => {
      // Invalidate all property queries to ensure fresh data
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.properties.root],
      });

      // Also invalidate events that might have this property embedded
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.events.root],
      });
    },
  });
};

export const usePropertyCreateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (req: Property) => {
      return await PropertyClientService.Create(req);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.properties.root],
      });
    },
  });
};
